import { pickAppCopy, type SupportedLocale } from "@patient-watch/i18n";
import { useAppLocale } from "./LocaleContext";
import { journeyCopyEn } from "@patient-watch/i18n/webapp/en-GB/journeyCopy";
import { journeyCopyDe } from "@patient-watch/i18n/webapp/de-DE/journeyCopy";
import { journeyCopyDa } from "@patient-watch/i18n/webapp/da-DK/journeyCopy";
import { journeyCopyEnUs } from "@patient-watch/i18n/webapp/en-US/journeyCopy";
import { journeyCopyFr } from "@patient-watch/i18n/webapp/fr-FR/journeyCopy";
import { journeyCopyIt } from "@patient-watch/i18n/webapp/it-IT/journeyCopy";
import { journeyCopyEs } from "@patient-watch/i18n/webapp/es-ES/journeyCopy";

export interface JourneyCopy {
    profileCompleteTitle: string;
    profileCompleteStatusLabel: string;
    profileCompleteConsentDescription: string;
    profileCompleteSetupDescription: string;
    profileCompleteFormsDescription: string;
    profileCompleteDiaryDescription: string;
    consentCompleteTitle: string;
    consentCompleteStatusLabel: string;
    consentCompleteSetupDescription: string;
    consentCompleteFormsDescription: string;
    consentCompleteDiaryDescription: string;
    setupCompleteTitle: string;
    setupCompleteStatusLabel: string;
    setupCompleteConsentDescription: string;
    setupCompleteFormsDescription: string;
    setupCompleteDiaryDescription: string;
    formCompleteTitle: string;
    formCompleteNextDescription: string;
    formCompleteAllDoneDescription: string;
    /** Headline when every due form is finished. */
    formCompleteAllCaughtUpTitle: string;
    /** Body when every due form is finished and a later entry is scheduled. */
    formCompleteAllCaughtUpNextDue: (date: string) => string;
    /** Appended when up to date and a future entry is scheduled. */
    formCompleteNextEntryDue: (scheduleLabel: string, date: string) => string;
    formCompleteStatusSuffix: string;
    continueToNextForm: string;
    goToConsent: string;
    goToSetup: string;
    /** Progress card when procedure date is the next outstanding set-up step. */
    knowProcedureDateYetTitle: string;
    knowProcedureDateYetMessage: string;
    addProcedureDate: string;
    addHospitalNumber: string;
    addSide: string;
    addEventDate: (eventPhrase: string) => string;
    reviewConsent: string;
    shortcutsHeading: string;
    goToForms: string;
    goToYourDiary: string;
    goToYourResults: string;
    seeCharts: string;
    /** Journey progress bar phase labels */
    phaseProfile: string;
    phaseSetup: string;
    phaseConsent: string;
    phaseForms: string;
    phaseDiary: string;
    setupHospitalNumber: string;
    setupProcedureDate: string;
    setupSide: string;
    loadingProgress: string;
    /** Uppercase heading above the journey bar */
    progressTitle: string;
    /** Accessible label for dismissing the overview progress card */
    dismissProgress: string;
    /** Prefix before the active phase name, e.g. "Current stage: Profile" */
    currentStage: string;
    stepProgressAriaLabel: (stepLabel: string) => string;
    /** e.g. "of" in "1 of 5" */
    stepOf: string;
    /** Heading on the compact card that offers the one next task */
    nextStep: string;
    /** One line on the form-complete screen, e.g. "2 of 6 forms done" */
    formsDoneCount: (done: number, total: number) => string;
    /** Shown when the new patient's diary has not appeared after a short wait */
    diaryStillSettingUp: string;
    tryAgain: string;
}

const journeyCopy: Record<SupportedLocale, JourneyCopy> = {
    "en-GB": journeyCopyEn,
    "de-DE": journeyCopyDe,
    "da-DK": journeyCopyDa,
    "en-US": journeyCopyEnUs,
    "fr-FR": journeyCopyFr,
    "it-IT": journeyCopyIt,
    "es-ES": journeyCopyEs,
};

export function useJourneyCopy(): JourneyCopy {
    const { locale } = useAppLocale();
    return pickAppCopy(journeyCopy, locale);
}

export function getJourneyCopy(locale: SupportedLocale): JourneyCopy {
    return pickAppCopy(journeyCopy, locale);
}
